import { Eye, EyeOff, LogOut, Mail, ShieldCheck } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  buildAuthCallbackUrl,
  buildForgotPasswordPath,
  buildInviteLoginPath,
  clearPendingInvitation,
  resolveInvitationRecovery,
  storePendingInvitation,
  shouldFallbackToInviteSignin,
} from "../lib/invitation-flow";
import {
  clearInviteDiagnostics,
  recordInviteDiagnostic,
} from "../lib/invite-diagnostics";
import { betterAuthRequest } from "../lib/auth";
import { useAuth } from "../hooks/useAuth";
import { APP_HOME_PATH } from "../routes";

function InviteSummary({
  invitedEmail,
  organizationName,
}: {
  invitedEmail: string;
  organizationName: string;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-[linear-gradient(180deg,#fcfcfd_0%,#f8fafc_100%)] p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">
            Invitation
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
            Join {organizationName || "this workspace"}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
            This link is reserved for the invited email address. Sign in with that account or
            create it first, and workspace access will be granted automatically.
          </p>
        </div>

        <div className="hidden rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 sm:block">
          Auto-accept after auth
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
            <Mail className="h-4 w-4" />
            Invited email
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {invitedEmail || "Use the email address from the invitation."}
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white px-4 py-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
            <ShieldCheck className="h-4 w-4" />
            What happens next
          </div>
          <div className="mt-2 text-sm text-slate-700">
            Authenticate, verify if prompted, and land directly inside the invited workspace.
          </div>
        </div>
      </div>
    </div>
  );
}

export function InvitePage() {
  const { user, login, logout, refresh, signup } = useAuth();
  const posthog = usePostHog();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectPath = searchParams.get("redirect") || APP_HOME_PATH;
  const invitationId = searchParams.get("invitation");
  const invitedEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  const organizationName = searchParams.get("org")?.trim() ?? "this workspace";
  const organizationId = searchParams.get("orgId")?.trim() ?? "";
  const wasVerified = searchParams.get("verified") === "1";
  const emailLocked = invitedEmail.length > 0;

  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);
  const [requiresDifferentAccount, setRequiresDifferentAccount] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inviteLoginPath = useMemo(
    () =>
      buildInviteLoginPath({
        invitationId,
        invitedEmail: invitedEmail || email.trim(),
        invitedOrganizationName: organizationName,
        invitedOrganizationId: organizationId || undefined,
        redirectPath,
      }),
    [email, invitationId, invitedEmail, organizationId, organizationName, redirectPath],
  );
  const forgotPasswordPath = useMemo(
    () =>
      buildForgotPasswordPath({
        redirectPath: inviteLoginPath,
        email: invitedEmail || email.trim(),
      }),
    [email, inviteLoginPath, invitedEmail],
  );

  const inferredName = useMemo(() => {
    const localPart = (invitedEmail || email).split("@")[0]?.trim() ?? "";
    if (!localPart) {
      return "Teammate";
    }

    return localPart
      .replace(/[._-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }, [email, invitedEmail]);

  useEffect(() => {
    setEmail(invitedEmail);
  }, [invitedEmail]);

  useEffect(() => {
    if (!invitationId) {
      return;
    }

    storePendingInvitation({
      invitationId,
      invitedEmail: invitedEmail || undefined,
      invitedOrganizationName: organizationName || undefined,
      invitedOrganizationId: organizationId || undefined,
      redirectPath,
    });
    recordInviteDiagnostic(posthog, "invite_context_stored", {
      invitationId,
      invitedEmail,
      organizationName,
      redirectPath,
    });
  }, [invitationId, invitedEmail, organizationId, organizationName, posthog, redirectPath]);

  useEffect(() => {
    if (wasVerified) {
      setNotice("Email verified. Finalizing your workspace access.");
      recordInviteDiagnostic(posthog, "invite_verification_returned", {
        invitationId,
        invitedEmail,
        organizationName,
        hasUser: Boolean(user),
      });
    }
  }, [invitationId, invitedEmail, organizationName, posthog, user, wasVerified]);

  useEffect(() => {
    if (!invitationId) {
      return;
    }

    recordInviteDiagnostic(posthog, "invite_page_opened", {
      invitationId,
      invitedEmail,
      organizationName,
      redirectPath,
      wasVerified,
      hasUser: Boolean(user),
    });
  }, [invitationId, invitedEmail, organizationName, posthog, redirectPath, user, wasVerified]);

  useEffect(() => {
    if (!user || !invitationId) {
      return;
    }

    let cancelled = false;

    const acceptInvitation = async () => {
      setAcceptingInvitation(true);
      setRequiresDifferentAccount(false);
      setError(null);
      setNotice(`Adding you to ${organizationName}...`);
      recordInviteDiagnostic(posthog, "invite_accept_started", {
        invitationId,
        signedInEmail: user.email.toLowerCase(),
        invitedEmail,
      });

      try {
        await betterAuthRequest("/organization/accept-invitation", {
          method: "POST",
          body: JSON.stringify({ invitationId }),
        });

        await refresh();

        if (!cancelled) {
          clearPendingInvitation();
          recordInviteDiagnostic(posthog, "invite_accept_succeeded", {
            invitationId,
            signedInEmail: user.email.toLowerCase(),
            redirectPath,
          });
          clearInviteDiagnostics();
          window.location.assign(redirectPath);
        }
      } catch (acceptError) {
        if (cancelled) {
          return;
        }

        const recovery = resolveInvitationRecovery(
          acceptError instanceof Error ? acceptError.message : "Unable to accept invitation.",
        );

        if (recovery.action === "redirect") {
          clearPendingInvitation();
          recordInviteDiagnostic(posthog, "invite_accept_redirected", {
            invitationId,
            signedInEmail: user.email.toLowerCase(),
            error: recovery.error,
          });
          setNotice(recovery.error);
          window.location.assign(redirectPath);
          return;
        }

        if (recovery.action === "invalid") {
          clearPendingInvitation();
          const alreadyMember =
            (Boolean(organizationId) &&
              user.organizations.some((organization) => organization.id === organizationId)) ||
            (!organizationId &&
              Boolean(organizationName) &&
              user.organizations.some(
                (organization) =>
                  organization.name.trim().toLowerCase() ===
                  organizationName.trim().toLowerCase(),
              ));

          if (alreadyMember) {
            recordInviteDiagnostic(posthog, "invite_invalid_but_member_redirected", {
              invitationId,
              signedInEmail: user.email.toLowerCase(),
              organizationName,
            });
            setNotice("This invitation is no longer pending. Redirecting to the workspace.");
            window.location.assign(redirectPath);
            return;
          }

          recordInviteDiagnostic(posthog, "invite_invalid", {
            invitationId,
            signedInEmail: user.email.toLowerCase(),
            error: recovery.error,
          });
          setError(recovery.error);
          setAcceptingInvitation(false);
          return;
        }

        setRequiresDifferentAccount(recovery.action === "switch-account");
        recordInviteDiagnostic(posthog, "invite_accept_failed", {
          invitationId,
          signedInEmail: user.email.toLowerCase(),
          invitedEmail,
          error: recovery.error,
          action: recovery.action,
        });
        setError(recovery.error);
        setAcceptingInvitation(false);
      }
    };

    void acceptInvitation();

    return () => {
      cancelled = true;
    };
  }, [invitationId, organizationName, redirectPath, refresh, user]);

  if (!invitationId) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  const resendVerificationEmail = async (targetEmail: string) => {
    await betterAuthRequest("/send-verification-email", {
      method: "POST",
      body: JSON.stringify({
        email: targetEmail.trim().toLowerCase(),
        callbackURL: buildAuthCallbackUrl(window.location.origin, {
          invitationId,
          invitedEmail: invitedEmail || targetEmail,
          invitedOrganizationName: organizationName,
          invitedOrganizationId: organizationId || undefined,
          redirectPath,
        }),
      }),
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setRequiresDifferentAccount(false);
    setError(null);
    setNotice(null);

    try {
      recordInviteDiagnostic(posthog, "invite_submit_started", {
        invitationId,
        email: email.trim().toLowerCase(),
        invitedEmail,
        emailLocked,
      });

      try {
        const result = await signup({
          name: name.trim() || inferredName,
          email: email.trim(),
          password,
          invitationId,
          invitedEmail: invitedEmail || email.trim(),
          invitedOrganizationName: organizationName,
          invitedOrganizationId: organizationId || undefined,
          redirectPath,
        });

        if (result.status === "verification-required") {
          setPassword("");
          recordInviteDiagnostic(posthog, "invite_signup_verification_required", {
            invitationId,
            email: result.email,
            emailSent: result.emailSent,
          });
          setNotice(
            result.emailSent
              ? `Check ${result.email} for a verification link. After verifying, return here and sign in if you are not brought back automatically.`
              : `Verify ${result.email} before joining ${organizationName}. If you do not see a verification email, sign in again and we will resend it.`,
          );
          return;
        }

        if (result.status === "signin-required") {
          setPassword("");
          recordInviteDiagnostic(posthog, "invite_signup_signin_required", {
            invitationId,
            email: result.email,
            loginPath: inviteLoginPath,
          });
          window.location.assign(inviteLoginPath);
          return;
        }
      } catch (signupError) {
        const signupMessage =
          signupError instanceof Error ? signupError.message : "Unable to continue";

        if (!shouldFallbackToInviteSignin(signupMessage)) {
          throw signupError;
        }

        await login(email.trim(), password, {
          invitationId,
          invitedEmail: invitedEmail || email.trim(),
          invitedOrganizationName: organizationName,
          invitedOrganizationId: organizationId || undefined,
          redirectPath,
        });
        recordInviteDiagnostic(posthog, "invite_signup_fell_back_to_login", {
          invitationId,
          email: email.trim().toLowerCase(),
        });
      }

      setNotice(`Continuing. Adding you to ${organizationName}...`);
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : "Unable to continue";

      if (message === "Email not verified") {
        try {
          await resendVerificationEmail(email);
          recordInviteDiagnostic(posthog, "invite_resend_verification_succeeded", {
            invitationId,
            email: email.trim().toLowerCase(),
          });
          setNotice(
            `Verify ${email.trim().toLowerCase()} first, then sign in to finish joining ${organizationName}.`,
          );
          return;
        } catch {
          setError("Email not verified. Unable to resend the verification email.");
          return;
        }
      }

      recordInviteDiagnostic(posthog, "invite_submit_failed", {
        invitationId,
        email: email.trim().toLowerCase(),
        error: message,
      });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#f8fbff_0%,#f3f6ea_36%,#f8f5ed_100%)] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-900">
            Datascope
          </div>
          <Link
            to={inviteLoginPath}
            className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline"
          >
            Standard sign in
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <InviteSummary invitedEmail={invitedEmail} organizationName={organizationName} />

          <div className="rounded-md border border-black/5 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.10)] sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">
              Access
            </div>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
              {acceptingInvitation ? "Finalizing invitation" : "Open your invitation"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {acceptingInvitation
                ? "Your account is authenticated. Workspace membership is being applied now."
                : "Continue with the invited email address. If an account already exists, you will be signed in. Otherwise, one will be created and connected to this workspace."}
            </p>

            {!user ? (
              <>
                <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    This invitation is tied to{" "}
                    <span className="font-semibold">{invitedEmail || "the invited email"}</span>.
                  </div>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Name</span>
                    <input
                      type="text"
                      value={name}
                      disabled={submitting}
                      onChange={(event) => setName(event.target.value)}
                      className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-950"
                      placeholder={inferredName}
                    />
                    <span className="mt-2 block text-xs text-slate-500">
                      Only used if a new account needs to be created.
                    </span>
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Email</span>
                    <input
                      type="email"
                      value={email}
                      disabled={emailLocked}
                      onChange={(event) => setEmail(event.target.value)}
                      className="block w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none"
                      placeholder="teammate@company.com"
                      required
                    />
                  </label>

                  <label className="block">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Password</span>
                      <Link
                        to={forgotPasswordPath}
                        className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        minLength={8}
                        disabled={submitting}
                        onChange={(event) => setPassword(event.target.value)}
                        className="block w-full rounded-md border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-950"
                        placeholder="Enter a password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-600"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </label>

                  {error ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {error}
                    </div>
                  ) : null}

                  {notice ? (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                      {notice}
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={submitting || acceptingInvitation}
                    className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-[#005f8a] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? "Processing..." : "Continue"}
                  </button>
                </form>
              </>
            ) : (
              <div className="mt-8 space-y-4">
                {error ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                ) : null}

                {notice ? (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {notice}
                  </div>
                ) : null}

                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  Signed in as <span className="font-semibold text-slate-900">{user.email}</span>
                </div>

                {requiresDifferentAccount ? (
                  <button
                    type="button"
                    onClick={() => void logout()}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out and use another account
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
